// Singles (light) "Close wave" — the modal behind the Pause button.
//
// A Singles wave reserves up to 80 orders when it starts. Before this,
// tapping Pause was the only way off the screen and it kept every one of
// those orders locked to the wave — a picker who had to stop half-way
// stranded 40 orders nobody else could pick. Now the Pause tap asks:
//
//   • Pause      — as before: every order stays on this wave for resume.
//   • Close wave — finish here. Orders already picked go to dispatch
//                  exactly as a natural completion would; orders never
//                  started go straight back to the pick queue for anyone.
//
// The close is refused while any order is PART-picked: its units are
// already in the mixed tote, so the picker must finish (or resolve via
// the missing flow) those orders first. The blocked state lists them.
//
// Presentational only. The parent drives `step` from the preview /
// close calls (POST /picking/wave/:id/close, dry_run first).
import { X, Pause, PackageCheck, AlertTriangle, Undo2 } from "lucide-react";
import { useTranslation } from "../../i18n";
import "./CloseWaveModal.css";

export interface PartialOrder {
    order_id: string;
    order_number: string | null;
    picked: number;
    total: number;
}

export type CloseWaveStep =
    | { step: "choice" }
    | { step: "loading" }
    | { step: "confirm"; done: number; untouched: number }
    | { step: "blocked"; partial: PartialOrder[] };

interface Props {
    state: CloseWaveStep;
    busy: boolean;
    /** Choice → plain pause (unchanged behaviour). */
    onPause: () => void;
    /** Choice → ask the BE for the preview. */
    onRequestClose: () => void;
    /** Confirm → do it. */
    onConfirmClose: () => void;
    /** Any step → dismiss, keep picking. */
    onCancel: () => void;
}

const CloseWaveModal = ({ state, busy, onPause, onRequestClose, onConfirmClose, onCancel }: Props) => {
    const { t } = useTranslation();
    const title =
        state.step === "blocked" ? t("picking_close_blocked_title")
        : state.step === "confirm" ? t("picking_close_title")
        : t("picking_pause_or_close_title");

    return (
        <div className="modal-overlay" onClick={() => !busy && onCancel()}>
            <div className="modal-card close-wave-modal" onClick={e => e.stopPropagation()} role="dialog" aria-label={title}>
                <div className="modal-card-header">
                    <h2>{title}</h2>
                    <button className="modal-close" onClick={onCancel} disabled={busy} aria-label={t("cancel")}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                    {state.step === "choice" && (
                        <>
                            <div className="close-wave-hint">{t("picking_pause_or_close_hint")}</div>
                            <div className="close-wave-choices">
                                <button className="btn btn-ghost btn-lg close-wave-choice" onClick={onPause} disabled={busy}>
                                    <Pause size={20} />
                                    <span>
                                        <strong>{t("picking_pause_btn")}</strong>
                                        <small>{t("picking_pause_choice_sub")}</small>
                                    </span>
                                </button>
                                <button className="btn btn-primary btn-lg close-wave-choice" onClick={onRequestClose} disabled={busy}>
                                    <PackageCheck size={20} />
                                    <span>
                                        <strong>{t("picking_close_wave_btn")}</strong>
                                        <small>{t("picking_close_choice_sub")}</small>
                                    </span>
                                </button>
                            </div>
                        </>
                    )}

                    {state.step === "loading" && (
                        <div className="close-wave-hint" role="status">{t("picking_close_loading")}</div>
                    )}

                    {state.step === "confirm" && (
                        <>
                            <ul className="close-wave-summary">
                                <li className="close-wave-summary-done">
                                    <PackageCheck size={18} />
                                    <span>{t("picking_close_done_line", { count: String(state.done) })}</span>
                                </li>
                                <li className="close-wave-summary-back">
                                    <Undo2 size={18} />
                                    <span>{t("picking_close_untouched_line", { count: String(state.untouched) })}</span>
                                </li>
                            </ul>
                            <div className="modal-confirm-buttons close-wave-actions">
                                <button className="btn btn-ghost btn-lg" onClick={onCancel} disabled={busy}>
                                    {t("picking_close_keep")}
                                </button>
                                <button className="btn btn-primary btn-lg" onClick={onConfirmClose} disabled={busy}>
                                    {busy ? t("submitting") : t("picking_close_confirm")}
                                </button>
                            </div>
                        </>
                    )}

                    {state.step === "blocked" && (
                        <>
                            <div className="close-wave-blocked-hint">
                                <AlertTriangle size={18} />
                                <span>{t("picking_close_blocked_body")}</span>
                            </div>
                            <ul className="close-wave-partial-list">
                                {state.partial.map(o => (
                                    <li key={o.order_id} className="close-wave-partial-row">
                                        <span className="close-wave-partial-order">{o.order_number ?? o.order_id}</span>
                                        <span className="close-wave-partial-count">
                                            {t("picking_close_blocked_row", { picked: String(o.picked), total: String(o.total) })}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <div className="modal-confirm-buttons close-wave-actions">
                                <button className="btn btn-primary btn-lg" onClick={onCancel}>
                                    {t("picking_close_keep")}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CloseWaveModal;
